import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  message,
  Space,
  Alert,
  Divider,
} from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

interface ActivityIPDeviceConfig {
  sameIpLimit: number | null; // 限制同ip数量，null表示不限制
  sameDeviceLimit: number | null; // 限制同设备数量，null表示不限制
  selectedActivities: string[]; // 选中的活动ID列表，空数组表示全选
  whitelistIps: string[]; // 白名单IP列表
  whitelistDevices: string[]; // 白名单设备列表
}

interface ActivityIPDeviceFormData {
  sameIpLimit: number | null;
  sameDeviceLimit: number | null;
  selectedActivities: string[];
  whitelistIps: string; // 表单中使用逗号分隔的字符串
  whitelistDevices: string; // 表单中使用逗号分隔的字符串
}

interface Activity {
  id: string;
  name: string;
  code: string;
}

const ActivityIPDeviceSettings: React.FC = () => {
  const [form] = Form.useForm<ActivityIPDeviceFormData>();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectAll, setSelectAll] = useState(true);

  // 加载活动列表
  useEffect(() => {
    loadActivities();
    loadConfig();
  }, []);

  const loadActivities = async () => {
    try {
      // TODO: 替换为实际API调用
      // const response = await api.get('/activities');
      // setActivities(response.data);
      
      // 模拟数据
      const mockActivities: Activity[] = [
        { id: '1', name: '新人注册奖励', code: 'NEW_USER_REWARD' },
        { id: '2', name: '首充奖励', code: 'FIRST_DEPOSIT_REWARD' },
        { id: '3', name: '每日签到', code: 'DAILY_CHECKIN' },
        { id: '4', name: 'VIP奖励', code: 'VIP_REWARD' },
        { id: '5', name: '转盘活动', code: 'TURNTABLE' },
        { id: '6', name: '红包雨', code: 'RED_ENVELOPE_RAIN' },
      ];
      setActivities(mockActivities);
    } catch (error) {
      console.error('加载活动列表失败:', error);
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      // TODO: 替换为实际API调用
      // const response = await api.get('/activity-ip-device-config');
      // const config = response.data;
      
      // 模拟数据
      const mockConfig: ActivityIPDeviceConfig = {
        sameIpLimit: 4,
        sameDeviceLimit: 4,
        selectedActivities: [], // 空数组表示全选
        whitelistIps: [],
        whitelistDevices: [],
      };
      
      form.setFieldsValue({
        sameIpLimit: mockConfig.sameIpLimit,
        sameDeviceLimit: mockConfig.sameDeviceLimit,
        selectedActivities: mockConfig.selectedActivities.length === 0 ? ['ALL'] : mockConfig.selectedActivities,
        whitelistIps: mockConfig.whitelistIps.length > 0 ? mockConfig.whitelistIps.join(',') : '',
        whitelistDevices: mockConfig.whitelistDevices.length > 0 ? mockConfig.whitelistDevices.join(',') : '',
      });
      
      setSelectAll(mockConfig.selectedActivities.length === 0);
    } catch (error) {
      console.error('加载配置失败:', error);
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: ActivityIPDeviceFormData) => {
    try {
      setLoading(true);
      
      const config: ActivityIPDeviceConfig = {
        sameIpLimit: values.sameIpLimit || null,
        sameDeviceLimit: values.sameDeviceLimit || null,
        selectedActivities: selectAll || values.selectedActivities?.includes('ALL') 
          ? [] 
          : values.selectedActivities?.filter((id: string) => id !== 'ALL') || [],
        whitelistIps: values.whitelistIps 
          ? values.whitelistIps.split(',').map((ip: string) => ip.trim()).filter((ip: string) => ip)
          : [],
        whitelistDevices: values.whitelistDevices
          ? values.whitelistDevices.split(',').map((device: string) => device.trim()).filter((device: string) => device)
          : [],
      };
      
      // TODO: 替换为实际API调用
      // await api.post('/activity-ip-device-config', config);
      
      console.log('保存配置:', config);
      message.success('配置保存成功');
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    loadConfig();
  };

  const handleActivitySelectChange = (value: string[]) => {
    if (value.includes('ALL')) {
      setSelectAll(true);
      form.setFieldsValue({ selectedActivities: ['ALL'] });
    } else {
      setSelectAll(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="活动IP/设备设置"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={loading}
            >
              确认
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            sameIpLimit: null,
            sameDeviceLimit: null,
            selectedActivities: ['ALL'],
            whitelistIps: '',
            whitelistDevices: '',
          }}
        >
          <Form.Item
            label="限制同ip数量"
            name="sameIpLimit"
            tooltip="填写数字，则对应活动奖励，同ip用户只有前N个可以触发奖励，其余用户阻拦。留空或填0表示不限制。"
            rules={[
              { type: 'number', min: 0, message: '数量必须大于等于0' },
            ]}
          >
            <InputNumber
              min={0}
              placeholder="请输入限制数量（留空或0表示不限制）"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Alert
            message="说明"
            description="填写4,则对应活动奖励,同ip用户只有前4个可以触发奖励,其余用户阻拦"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="限制同设备数量"
            name="sameDeviceLimit"
            tooltip="填写数字，则对应活动奖励，同设备用户只有前N个可以触发奖励，其余用户阻拦。留空或填0表示不限制。"
            rules={[
              { type: 'number', min: 0, message: '数量必须大于等于0' },
            ]}
          >
            <InputNumber
              min={0}
              placeholder="请输入限制数量（留空或0表示不限制）"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Alert
            message="说明"
            description="填写4,则对应活动奖励,同设备用户只有前4个可以触发奖励,其余用户阻拦"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="活动选择"
            name="selectedActivities"
            tooltip="可以全选活动,可以只针对需要限制ip的活动。未选择的活动,不受ip/设备限制"
          >
            <Select
              mode="multiple"
              placeholder="请选择活动（全选表示所有活动）"
              onChange={handleActivitySelectChange}
              style={{ width: '100%' }}
            >
              <Option value="ALL">全选</Option>
              {activities.map(activity => (
                <Option key={activity.id} value={activity.id}>
                  {activity.name} ({activity.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Alert
            message="说明"
            description="可以全选活动,可以只针对需要限制ip的活动。未选择的活动,不受ip/设备限制"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="白名单ip"
            name="whitelistIps"
            tooltip="多个ip,英文逗号隔开。加入白名单ip,不受同ip限制"
          >
            <TextArea
              rows={3}
              placeholder="请输入IP地址，多个IP用英文逗号分隔，例如：192.168.1.1,192.168.1.2"
            />
          </Form.Item>
          <Alert
            message="说明"
            description="多个ip,英文逗号隔开。加入白名单ip,不受同ip限制"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            label="白名单设备"
            name="whitelistDevices"
            tooltip="多个设备ID,英文逗号隔开。加入白名单设备,不受同设备限制"
          >
            <TextArea
              rows={3}
              placeholder="请输入设备ID，多个设备ID用英文逗号分隔，例如：device1,device2"
            />
          </Form.Item>
          <Alert
            message="说明"
            description="多个设备ID,英文逗号隔开。加入白名单设备,不受同设备限制"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        </Form>
      </Card>
    </div>
  );
};

export default ActivityIPDeviceSettings;

